import type { ConfirmedCheckDestination } from "@/domain/check-destinations";

interface CheckTransaction {
	id: string;
	description: string;
	amountCents: number;
	occurredOn: string;
	direction: "DEBIT" | "CREDIT";
	bankStatus: "POSTED" | "PENDING";
	classification: { destination: string } | null;
	checkDestination: ConfirmedCheckDestination | null;
}

type IdentifiedCheckMovement = NonNullable<ReturnType<typeof identifyCheckMovement>>;

export const Checks = {
	list(transactions: CheckTransaction[]) {
		return transactions.flatMap((transaction) => {
			if (transaction.direction !== "DEBIT" || transaction.bankStatus !== "POSTED") {
				return [];
			}

			const movement = identifyCheckMovement(transaction);

			return movement ? [movement] : [];
		});
	},

	isOutboundFreightReconciled(transaction: CheckTransaction) {
		if (transaction.direction !== "DEBIT" || transaction.bankStatus !== "POSTED") {
			return false;
		}

		const movement = identifyCheckMovement(transaction);

		return (
			movement?.kind !== "RETURNED" &&
			movement?.checkDestination?.destinationId === "OUTBOUND_FREIGHT"
		);
	},

	summarize(transactions: CheckTransaction[]) {
		const movements = Checks.list(transactions);
		const issued = movements.filter((movement) => movement.kind !== "RETURNED");
		const compensated = movements.filter((movement) => movement.kind === "COMPENSATED");
		const cashWithdrawals = movements.filter((movement) => movement.kind === "CASH_WITHDRAWAL");
		const returned = movements.filter((movement) => movement.kind === "RETURNED");
		const unassigned = issued.filter(
			(movement) => !movement.classification && !movement.checkDestination,
		);
		const cashAccount = issued.filter(
			(movement) => movement.checkDestination?.destinationId === "CASH_ACCOUNT",
		);
		const outboundFreight = issued.filter(
			(movement) => movement.checkDestination?.destinationId === "OUTBOUND_FREIGHT",
		);

		return {
			total: totals(issued),
			compensated: totals(compensated),
			cashWithdrawals: totals(cashWithdrawals),
			returned: totals(returned),
			unassigned: totals(unassigned),
			cashAccount: totals(cashAccount),
			outboundFreight: totals(outboundFreight),
			movements,
		};
	},

	number(description: string) {
		return extractCheckNumber(description.trim());
	},

	settlementNumber(description: string) {
		const movement = identifyCheckDescription(description.trim());

		return movement?.kind === "RETURNED" ? null : (movement?.checkNumber ?? null);
	},

	movement(description: string) {
		return identifyCheckDescription(description.trim());
	},

	groupByDate(movements: IdentifiedCheckMovement[]) {
		return [...Map.groupBy(movements, (movement) => movement.occurredOn)]
			.map(([date, items]) => {
				const total = totals(items);

				return { date, count: total.count, amountCents: total.amountCents, items };
			})
			.toSorted((left, right) => right.date.localeCompare(left.date));
	},
};

function identifyCheckMovement(transaction: CheckTransaction) {
	const description = transaction.description.trim();
	const identified = identifyCheckDescription(description);

	if (!identified) {
		return null;
	}

	return {
		...baseMovement(transaction, identified.checkNumber),
		...identified.details,
		kind: identified.kind,
	};
}

function identifyCheckDescription(description: string) {
	const compensated = /^CH COMPENSADO\s+(?<bankCode>\d{3})\s+(?<checkNumber>\d+)$/u.exec(
		description,
	);

	if (compensated?.groups) {
		return {
			kind: "COMPENSATED" as const,
			checkNumber: compensated.groups.checkNumber.padStart(6, "0"),
			details: { bankCode: compensated.groups.bankCode },
		};
	}

	const cashWithdrawal = /^SAQ DIN AG(?<branch>\d+) CHQ(?<checkNumber>\d+)$/u.exec(description);

	if (cashWithdrawal?.groups) {
		return {
			kind: "CASH_WITHDRAWAL" as const,
			checkNumber: cashWithdrawal.groups.checkNumber.padStart(6, "0"),
			details: { branch: cashWithdrawal.groups.branch },
		};
	}

	const returned =
		/^DEV CH NAC\s+(?<bankCode>\d{3})\s+(?<checkNumber>\d+)\s+(?<returnCode>\d+)$/u.exec(
			description,
		);

	if (returned?.groups) {
		return {
			kind: "RETURNED" as const,
			checkNumber: returned.groups.checkNumber.padStart(6, "0"),
			details: {
				bankCode: returned.groups.bankCode,
				returnCode: returned.groups.returnCode,
			},
		};
	}

	return null;
}

function baseMovement(transaction: CheckTransaction, number: string) {
	return {
		id: transaction.id,
		checkNumber: number,
		description: transaction.description,
		amountCents: Math.abs(transaction.amountCents),
		occurredOn: transaction.occurredOn,
		classification: transaction.classification,
		checkDestination: transaction.checkDestination,
	};
}

function extractCheckNumber(description: string) {
	const patterns = [
		/^CH COMPENSADO\s+\d{3}\s+(?<checkNumber>\d+)$/u,
		/^SAQ DIN AG\d+ CHQ(?<checkNumber>\d+)$/u,
		/^DEV CH NAC\s+\d{3}\s+(?<checkNumber>\d+)\s+\d+$/u,
	];

	for (const pattern of patterns) {
		const match = pattern.exec(description);

		if (match?.groups) {
			return match.groups.checkNumber.padStart(6, "0");
		}
	}

	return null;
}

function totals(items: { amountCents: number }[]) {
	return {
		count: items.length,
		amountCents: items.reduce((total, item) => total + item.amountCents, 0),
	};
}
